import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, X } from 'lucide-react';

export type StatusType = 'success' | 'error' | 'warning' | 'info';

export interface StatusMessageProps {
  type: StatusType;
  message: string;
  isVisible: boolean;
  onClose: () => void;
  autoHideDelay?: number; // in milliseconds
}

const StatusMessage: React.FC<StatusMessageProps> = ({
  type,
  message,
  isVisible,
  onClose,
  autoHideDelay = 5000 // Default to 5 seconds
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const rafCancelRef = useRef(false);

  useEffect(() => {
    if (isVisible) {
      rafCancelRef.current = false;
      // Start in "out" state so the next paint shows the card off-screen
      setIsAnimating(false);
      // Defer "in" state to next frame so the browser paints the initial state first, then the transition runs
      const rafId = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!rafCancelRef.current) {
            setIsAnimating(true);
          }
        });
      });

      // Auto-hide after delay
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setTimeout(onClose, 500); // Wait for slide out animation
      }, autoHideDelay);

      return () => {
        rafCancelRef.current = true;
        clearTimeout(timer);
        cancelAnimationFrame(rafId);
      };
    }
  }, [isVisible, autoHideDelay, onClose]);

  if (!isVisible) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'info':
        return <AlertCircle className="w-5 h-5 text-blue-500" />;
      default:
        return null;
    }
  };

  const getBackgroundColor = () => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getTextColor = () => {
    switch (type) {
      case 'success':
        return 'text-green-800';
      case 'error':
        return 'text-red-800';
      case 'warning':
        return 'text-yellow-800';
      case 'info':
        return 'text-blue-800';
      default:
        return 'text-gray-800';
    }
  };

  return (
    <div
      className="fixed inset-x-4 z-[9999] flex justify-end overflow-hidden pointer-events-none"
      style={{ top: 'max(1.5rem, env(safe-area-inset-top))' }}
    >
      <div
        className={`${getBackgroundColor()} border rounded-lg shadow-lg p-4 flex items-start gap-3 w-full max-w-md pointer-events-auto transition-all duration-500 ease-in-out ${
          isAnimating ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'
        }`}
      >
        {getIcon()}
        <div className="flex-1">
          <p className={`${getTextColor()} font-medium`}>{message}</p>
        </div>
        <button
          onClick={() => {
            setIsAnimating(false);
            setTimeout(onClose, 500);
          }}
          className="text-gray-600 hover:text-gray-800 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default StatusMessage;
